import { useEffect } from "react";

/**
 * Watches the DOM for any Lovable branding (images, iframes, links, text)
 * and replaces it with the site's own branding (CrebtoPay + /favicon.png).
 * Runs continuously via MutationObserver + periodic sweep.
 */
const SITE_NAME = "CrebtoPay";
const SITE_LOGO = "/favicon.png";
const LOVABLE_RE = /lovable/i;

const sanitizeNode = (root: ParentNode) => {
  // Images referencing lovable assets/CDN
  root.querySelectorAll?.("img").forEach((img) => {
    const src = img.getAttribute("src") || "";
    const alt = img.getAttribute("alt") || "";
    if (LOVABLE_RE.test(src) || LOVABLE_RE.test(alt)) {
      img.setAttribute("src", SITE_LOGO);
      img.setAttribute("alt", SITE_NAME);
    }
  });

  // Anchor tags / badges linking to lovable.dev / lovable.app
  root.querySelectorAll?.("a").forEach((a) => {
    const href = a.getAttribute("href") || "";
    if (/lovable\.(dev|app|com)/i.test(href)) {
      (a as HTMLElement).style.display = "none";
    }
  });

  // Iframes from lovable domains
  root.querySelectorAll?.("iframe").forEach((f) => {
    const src = f.getAttribute("src") || "";
    if (LOVABLE_RE.test(src)) (f as HTMLElement).style.display = "none";
  });

  // Text nodes containing the word "Lovable"
  const walker = document.createTreeWalker(root as Node, NodeFilter.SHOW_TEXT);
  const toReplace: Text[] = [];
  let n: Node | null;
  while ((n = walker.nextNode())) {
    const t = n as Text;
    if (t.parentElement && ["SCRIPT", "STYLE"].includes(t.parentElement.tagName)) continue;
    if (LOVABLE_RE.test(t.nodeValue || "")) toReplace.push(t);
  }
  toReplace.forEach((t) => {
    t.nodeValue = (t.nodeValue || "").replace(/lovable/gi, SITE_NAME);
  });
};

const BrandingSanitizer = () => {
  useEffect(() => {
    // Initial sweep
    sanitizeNode(document.body);

    // Continuous observer for injected badges
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (node.nodeType === 1) sanitizeNode(node as Element);
          else if (node.nodeType === 3 && LOVABLE_RE.test(node.nodeValue || "")) {
            node.nodeValue = (node.nodeValue || "").replace(/lovable/gi, SITE_NAME);
          }
        });
        if (m.type === "attributes" && m.target.nodeType === 1) {
          sanitizeNode(m.target as Element);
        }
      }
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ["src", "alt", "href"],
    });

    // Safety sweep every 1s
    const interval = setInterval(() => sanitizeNode(document.body), 1000);

    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, []);

  return null;
};

export default BrandingSanitizer;
